/**
 * Service-worker RESPONSE CACHE teardown (Cache Storage).
 *
 * WHAT THIS EXISTS FOR. The PWA service worker parks HTTP responses in Cache
 * Storage under the fixed bucket names configured in `vite.config.ts`
 * (`workbox.runtimeCaching`). Cache Storage is ORIGIN-scoped and lives on
 * disk: it outlives the tab, the Supabase session, and both Web Storage areas
 * the „Запомни ме" switch moves that session between. Signing out already
 * tears down the mutation queue, the family context, every family-scoped store
 * and the realtime channels — the cached response bodies were the one thing
 * nothing cleared.
 *
 * WHY THE REST CACHE WAS DELETED RATHER THAN PURGED. `supabase-rest` used to
 * be a `NetworkFirst` matcher on `/rest/v1/**` with a 60-second TTL. PostgREST
 * does not send `Vary: Authorization`, so the Workbox cache key was the URL
 * alone and carried nothing about WHO asked. That is a soundness defect in the
 * key, not a lifecycle defect, and purging on the teardown paths does not
 * repair it:
 *
 *   - The commonest way a shared family device changes hands is not a logout
 *     at all. It is a closed tab: with „Запомни ме" off the session lives in
 *     `sessionStorage` and dies silently — no `SIGNED_OUT` event, no teardown
 *     of any kind — while the Cache Storage bodies, which are NOT
 *     session-scoped, stay on disk for the next person to read out of
 *     DevTools.
 *   - Even where a teardown does run it races the service worker: a read
 *     issued just before sign-out completes its `cache.put()` AFTER the purge
 *     has resolved, repopulating the bucket for whoever signs in next.
 *
 * What the matcher bought, measured rather than assumed, was a 60-second
 * read-through window — and this app does not need it. React Query already
 * holds the last successful rows in memory (`gcTime` 10 min,
 * `networkMode: 'offlineFirst'`, `refetchOnWindowFocus: false`), so a live
 * session that loses the network keeps rendering last-known data with the
 * service worker uninvolved; a failed refetch leaves `data` standing. No
 * feature store persists rows either (only `auth-storage` and the theme
 * persist anything), so a cold start while offline had nothing to hydrate
 * from and 60 seconds of cached GETs was never going to change that. The real
 * offline capability here is the IndexedDB MUTATION queue, which none of this
 * touches. So the matcher is gone from `vite.config.ts` and `/rest/v1/**`
 * falls through to Workbox's default `NetworkOnly`: authenticated reads are
 * not written to disk in the first place.
 *
 * WHAT IS STILL CACHED, AND WHY IT STILL NEEDS A PURGE. The two Storage
 * matchers survive on purpose — read the banners on them in `vite.config.ts`.
 * `supabase-storage-signed` in particular holds up to 100 private album
 * bodies for 7 days, and the argument written out there for keeping it ("a
 * per-device cache, on a device that has already rendered these photos,
 * behind the OS account of a member of one family") is an argument about the
 * INSIDE of one session. A logout or a family switch is exactly the moment
 * that argument stops holding, so both buckets are purged here.
 *
 * NOT COVERED, ON PURPOSE: the `workbox-expiration` IndexedDB database keeps
 * one `{url, timestamp}` row per cached entry. Deleting it from the page would
 * corrupt the bookkeeping of the buckets we keep, and it holds no response
 * bodies — only URLs.
 *
 * This module imports nothing, so it is reachable from the auth store and the
 * family store alike without a cycle (`npm run deps:check` is the gate).
 *
 * @module shared/lib/responseCache
 */

/**
 * Buckets that hold identity- or family-scoped bodies and are emptied on every
 * identity/scope transition: explicit logout, the listener-driven sign-out
 * (expiry, cross-tab), and every family rescope.
 */
export const SCOPED_RESPONSE_CACHES = [
  'supabase-storage-public',
  'supabase-storage-signed',
] as const;

/**
 * Buckets this build no longer writes, kept here only to evict what a
 * PREVIOUSLY DEPLOYED service worker left on the device.
 *
 * `supabase-rest` is the REST cache removed with this change;
 * `api-cache` was a generic `NetworkFirst` matching any https URL whose path
 * contained a `/api/` segment. It matched nothing this app ever requested —
 * `apiClient` issues mutations only, and Workbox routes `GET` alone — while
 * standing ready to cache opaque (`status: 0`) cross-origin bodies for 24
 * hours under the same URL-only key. It was removed for the same reason.
 *
 * These would otherwise sit at rest forever: `cleanupOutdatedCaches` only ever
 * touched the PRECACHE, and with no route left to match them the expiration
 * plugin's eviction pass never runs against them again.
 */
export const LEGACY_RESPONSE_CACHES = ['supabase-rest', 'api-cache'] as const;

/** Every bucket a teardown empties. */
export const PURGED_RESPONSE_CACHES: readonly string[] = [
  ...SCOPED_RESPONSE_CACHES,
  ...LEGACY_RESPONSE_CACHES,
];

/**
 * The slice of `CacheStorage` this module uses. Narrow on purpose: it keeps
 * the helper unit-testable against a plain object, with no `CacheStorage`
 * polyfill and no service worker.
 */
export interface CacheStorageLike {
  readonly delete: (cacheName: string) => Promise<boolean>;
}

/**
 * Resolve Cache Storage, or `null` where it does not exist.
 *
 * Absent in SSR and in the test environment (happy-dom implements no
 * `caches`), absent in browsers without service-worker support, and absent —
 * or throwing on mere property access, which is why this is wrapped — in
 * insecure contexts and in privacy modes that block site data. Same shape of
 * guard as `resolveStorage` in `shared/clients/supabaseClient`.
 */
export function resolveCacheStorage(): CacheStorageLike | null {
  try {
    const candidate: CacheStorage | undefined = globalThis.caches;
    return typeof candidate?.delete === 'function' ? candidate : null;
  } catch {
    return null;
  }
}

/**
 * Delete the named buckets, isolating individual failures the way
 * {@link module:shared/lib/storeReset} isolates reset callbacks: one bucket
 * that refuses to go must never stop the others, and a teardown must never
 * reject — every caller runs it from a `finally` or fire-and-forget.
 *
 * @returns the names that actually existed and were deleted
 */
async function deleteCaches(
  names: readonly string[],
  storage: CacheStorageLike | null
): Promise<readonly string[]> {
  if (!storage) {
    return [];
  }

  const purged: string[] = [];

  await Promise.all(
    names.map(async (name) => {
      try {
        if (await storage.delete(name)) {
          purged.push(name);
        }
      } catch (error) {
        console.error(`Response cache purge failed for "${name}":`, error);
      }
    })
  );

  return purged;
}

/**
 * Empty every scoped response cache. Call on logout, on the listener-driven
 * sign-out, and on every family rescope.
 *
 * @param storage - Cache Storage to act on; defaults to the ambient one and to
 *   a no-op where there is none
 */
export function purgeResponseCaches(
  storage: CacheStorageLike | null = resolveCacheStorage()
): Promise<readonly string[]> {
  return deleteCaches(PURGED_RESPONSE_CACHES, storage);
}

/**
 * Evict the buckets an older build's service worker left behind
 * ({@link LEGACY_RESPONSE_CACHES}).
 *
 * Called once per boot, from `checkAuth`, because the device that most needs
 * this is the one that NEVER logs out — the case {@link purgeResponseCaches}
 * by construction cannot reach. Deliberately not memoised: `caches.delete()`
 * on a name that does not exist resolves `false` immediately, and module state
 * that every test has to reset would cost more than the call it saves.
 */
export function purgeLegacyResponseCaches(
  storage: CacheStorageLike | null = resolveCacheStorage()
): Promise<readonly string[]> {
  return deleteCaches(LEGACY_RESPONSE_CACHES, storage);
}
